import React from 'react';
import SeoHead from '../seo/SeoHead';
import { staticPageMeta } from '../seo/metadata';
import { breadcrumbSchema } from '../seo/structuredData';

const INTER_STACK = "'Inter', system-ui, -apple-system, sans-serif";

// Cormorant Garamond wordmark — matches the Sovra tile on the Northlight portfolio.
const SOVRA_WORDMARK = {
  fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
  fontWeight: 600,
  letterSpacing: '0.15em'
};

const SovraLandingPage = () => {
  // Shared typographic tokens — identical treatment to the Phalanx page.
  const headingStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 200,
    letterSpacing: '-0.02em'
  };
  const bodyStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 300
  };
  const ctaStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 500,
    letterSpacing: '0.18em',
    fontSize: '0.7rem'
  };

  const features = [
    {
      title: 'AI-Powered Analysis',
      body: 'Advanced machine learning models ingest global macro data, fiscal releases, and policy signals to anticipate rating movements before they materialize.'
    },
    {
      title: 'Upside Identification',
      body: 'Pinpoint the specific reforms, fiscal adjustments, and structural changes most likely to trigger an upgrade, and quantify the expected impact on borrowing costs.'
    },
    {
      title: 'Strategic Advisory',
      body: 'Direct engagement with finance ministries and sovereign issuers on reform design, investor narrative, and positioning with the major rating agencies.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: INTER_STACK }}>
      <SeoHead
        {...staticPageMeta({
          title: 'SOVRA | Sovereign Ratings Advisory',
          description: 'SOVRA: sovereign ratings advisory.',
          path: '/sovra',
          ogImage: `${process.env.REACT_APP_SITE_URL || 'https://researchphalanx.com'}/sovra-logo.png`,
        })}
        jsonLd={breadcrumbSchema([
          { name: 'Phalanx', path: '/' },
          { name: 'SOVRA', path: '/sovra' },
        ])}
      />
      {/* Header — transparent, floats over the hero. Wordmark in Cormorant Garamond
          to mirror the Sovra tile on the Northlight portfolio. */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6">
        <div className="max-w-6xl mx-auto py-5 sm:py-7">
          <nav className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-5 sm:gap-6">
              <span
                className="text-white leading-none"
                style={{
                  ...SOVRA_WORDMARK,
                  fontSize: 'clamp(1.5rem, 3.6vw, 2.75rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                SOVRA
              </span>
            </div>
            <div
              className="flex items-center gap-4 sm:gap-6"
              style={{
                ...bodyStyle,
                fontSize: '0.85rem',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)'
              }}
            >
              <a href="#features" className="text-white/70 hover:text-white hidden sm:inline transition-colors">Features</a>
              <a
                href="mailto:Contact@researchphalanx.com?subject=Sovra Inquiry"
                className="text-white hover:text-white/70 transition-colors"
              >
                Contact
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero — full viewport with the three agencies overlaid at the bottom */}
      <section className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/sovra-bg.png')", backgroundSize: 'cover' }}
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="relative h-full flex flex-col">
          {/* Centered headline + subcopy + CTA */}
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-3xl">
              <h1
                className="text-white mb-6 leading-[1.15]"
                style={{
                  ...headingStyle,
                  fontSize: 'clamp(1.75rem, 4.2vw, 3rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                AI-powered sovereign ratings advisory.
              </h1>
              <p
                className="text-white/85 leading-relaxed max-w-2xl mx-auto"
                style={{
                  ...bodyStyle,
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                We help governments identify the policy changes that improve creditworthiness and reduce sovereign borrowing costs.
              </p>
              <a
                href="mailto:Contact@researchphalanx.com?subject=Sovra Inquiry"
                className="inline-flex items-center gap-2 text-white border-b border-white hover:border-gray-300 hover:text-gray-300 pb-0.5 uppercase transition-colors mt-8 sm:mt-10"
                style={ctaStyle}
              >
                Contact Us
                <span className="text-base">→</span>
              </a>
            </div>
          </div>

          {/* Rating agencies — lifted higher into the hero so the names read
              as headline credibility marks rather than a footer strip. */}
          <div className="pb-24 sm:pb-32 px-6">
            <div className="max-w-4xl mx-auto">
              <div
                className="text-center text-white/65 uppercase mb-6"
                style={{
                  ...bodyStyle,
                  fontSize: '0.72rem',
                  letterSpacing: '0.3em',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                Methodologies Replicated
              </div>
              <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
                {["Moody's", 'Fitch', 'S&P'].map((agency) => (
                  <div key={agency} className="px-2">
                    <div
                      className="text-white"
                      style={{
                        ...headingStyle,
                        fontWeight: 300,
                        fontSize: 'clamp(1.65rem, 3.6vw, 2.6rem)',
                        textShadow: '0 2px 20px rgba(0,0,0,0.35)'
                      }}
                    >
                      {agency}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview — single tile with the Sovra thesis */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-xl border border-gray-200 bg-[#FAF8F3] p-7 sm:p-10"
          >
            <div
              className="uppercase text-gray-500 mb-5"
              style={{ ...bodyStyle, fontSize: '0.65rem', letterSpacing: '0.2em' }}
            >
              Overview
            </div>
            <p
              className="text-gray-900 leading-relaxed"
              style={{ ...headingStyle, fontSize: 'clamp(1.05rem, 1.9vw, 1.35rem)' }}
            >
              We have replicated the methodologies of all three major rating agencies and layered forward-looking projections on top, giving governments and finance ministries a precise view of how close they are to their next rating threshold and which reforms will move them across it.
            </p>
          </div>
        </div>
      </section>

      {/* Features — three tiles that invert on hover */}
      <section id="features" className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-14 max-w-2xl">
            <div
              className="uppercase text-gray-500 mb-3"
              style={{ ...bodyStyle, fontSize: '0.65rem', letterSpacing: '0.2em' }}
            >
              Capabilities
            </div>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Quantitative rigor, sovereign-scale advisory.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="group rounded-xl border border-gray-200 bg-[#FAF8F3] hover:bg-gray-900 hover:border-gray-900 p-6 sm:p-7 cursor-pointer"
                style={{
                  transition:
                    'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                }}
              >
                <h3
                  className="text-gray-900 group-hover:text-white mb-3 leading-snug"
                  style={{
                    ...headingStyle,
                    fontWeight: 400,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.01em',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {f.title}
                </h3>
                <p
                  className="text-gray-600 group-hover:text-gray-300 leading-relaxed"
                  style={{
                    ...bodyStyle,
                    fontSize: '0.875rem',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h3
            className="text-gray-900 mb-5 leading-[1.2]"
            style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
          >
            Ready to optimize your sovereign rating?
          </h3>
          <p
            className="text-gray-600 mb-8 leading-relaxed"
            style={{ ...bodyStyle, fontSize: '1rem' }}
          >
            Get in touch to see how Sovra can support your strategy.
          </p>
          <a
            href="mailto:Contact@researchphalanx.com?subject=Sovra Inquiry"
            className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-500 pb-0.5 uppercase transition-colors"
            style={ctaStyle}
          >
            Contact Us
            <span className="text-base">→</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div
          className="max-w-5xl mx-auto px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 uppercase text-[0.6rem] sm:text-[0.68rem] tracking-[0.18em] sm:tracking-[0.28em]"
          style={{ fontFamily: INTER_STACK, fontWeight: 400 }}
        >
          <span
            className="text-gray-900 leading-none"
            style={{
              ...SOVRA_WORDMARK,
              fontSize: '1.15rem',
              letterSpacing: '0.18em'
            }}
          >
            SOVRA
          </span>
          <p className="text-gray-400 text-center">&copy; 2026 Sovra. A Northlight Technologies company.</p>
        </div>
      </footer>
    </div>
  );
};

export default SovraLandingPage;
